import type { AxiosInstance, AxiosResponse } from 'axios';

import api from '@api/index';
import { QUESTION_TYPES } from '@api/instant_survey';
import { camelizeData } from '@api/utilities/helpers';

import {
  transformContinuousSumResponses,
  transformEssayResponses,
  transformGridResponses,
  transformMaxDiffResponses,
  transformRankResponses,
  transformSurveyResponses,
  transformVideoResponses,
} from '../utilities/helpers';
import type { Question } from '@api/surveys/utilities/types';
import { getSurveyResponseRequestConfig } from './utilities/helpers';
import type {
  BarChartResponses,
  BarChartResponsesPayload,
  ReadSurveyResponseParams,
  SurveyResponsesCount,
  SurveyResponsesCountPayload,
} from './utilities/types';
import { transformObjectToCamelCase } from '@utilities';

const axios = api as AxiosInstance;

/**
 * read continuous sum responses
 */
export const readContinuousSumResponses = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/cont-sum',
  }).then(({ data }) => transformContinuousSumResponses(data));
};

/**
 * read essay responses
 */
export const readEssayResponses = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/essay-responses/table',
  }).then(({ data }) => transformEssayResponses(data));
};

/**
 * read grid responses
 */
export const readGridResponses = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/grid',
  }).then(({ data }) => transformGridResponses(data));
};

/**
 * read max diff responses
 */
export const readMaxDiffResponses = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/maxdiff',
  }).then(({ data }) => transformMaxDiffResponses(data));
};

/**
 * read max diff responses from calypso
 */
export const readMaxDiffCalypsoResponses = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/max-diff',
  }).then(({ data }) => transformMaxDiffResponses(data));
};

/**
 * read rank responses
 */
export const readRankResponses = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/rank',
  }).then(({ data }) => transformRankResponses(data));
};

/**
 * Calls response endpoints based on question types in survey and transforms the data for dashboard consumption
 */
export const readSurveyDashboardRequests = async ({
  includeCompletionRate = false,
  params,
  surveyQuestions,
}: {
  includeCompletionRate?: boolean;
  params: ReadSurveyResponseParams;
  surveyQuestions: Question[];
}) => {
  const questionTypeToResponseMap = {
    [QUESTION_TYPES.CHECKBOX.value]: readSurveyResponses,
    [QUESTION_TYPES.CONT_SUM.value]: readContinuousSumResponses,
    [QUESTION_TYPES.CONTSUM.value]: readContinuousSumResponses,
    [QUESTION_TYPES.ESSAY.value]: readEssayResponses,
    [QUESTION_TYPES.MAX_DIFF.externalType]: readMaxDiffResponses,
    [QUESTION_TYPES.MAX_DIFF.value]: readMaxDiffCalypsoResponses,
    [QUESTION_TYPES.NUMERIC.externalType]: readContinuousSumResponses,
    [QUESTION_TYPES.NUMERIC.value]: readContinuousSumResponses,
    [QUESTION_TYPES.RADIO.value]: readSurveyResponses,
    [QUESTION_TYPES.RANK.value]: readRankResponses,
    [QUESTION_TYPES.TABLE.value]: readGridResponses,
    [QUESTION_TYPES.VIDEO.value]: readVideoResponses,
    [QUESTION_TYPES['RANK-DRAGDROP'].value]: readRankResponses,
    [QUESTION_TYPES['TABLE-RADIO'].value]: readGridResponses,
  };

  const requestsMap = new Map();

  if (!surveyQuestions || surveyQuestions.length === 0) {
    Object.values(questionTypeToResponseMap).forEach((responseCall) => {
      if (responseCall) {
        requestsMap.set(responseCall.name, responseCall);
      }
    });
  } else {
    surveyQuestions.forEach((question) => {
      const responseCall = questionTypeToResponseMap[question.type];
      if (responseCall) {
        requestsMap.set(responseCall.name, responseCall);
      }
    });
  }

  const responseRequestNames = ['readSurveyResponseCounts'];
  const requests = [readSurveyResponseCounts(params)] as Promise<unknown>[];

  if (includeCompletionRate) {
    responseRequestNames.push('readCompletionRate');
    requests.push(readCompletionRate(params));
  }

  responseRequestNames.push(...Array.from(requestsMap.keys()));
  requests.push(...Array.from(requestsMap.values()).map((response) => response(params)));

  const results = await Promise.allSettled(requests);

  const responseCounts = results[0];
  const completionRate = includeCompletionRate ? results[1] : null;
  const responseResults = results.slice(includeCompletionRate ? 2 : 1);
  const responseResultStartIndex = includeCompletionRate ? 2 : 1;
  const anyResponseFulfilled = responseResults.some(
    (responseResult) => responseResult.status === 'fulfilled'
  );

  responseResults.forEach((responseResult, idx) => {
    if (responseResult.status === 'rejected') {
      console.warn(
        `[Dashboard] Failed to fetch ${responseRequestNames[idx + responseResultStartIndex]}:`,
        responseResult.reason
      );
    }
  });

  const responseData = responseResults
    .filter((responseResult) => responseResult.status === 'fulfilled')
    .flatMap((responseResult) => (Array.isArray(responseResult.value) ? responseResult.value : []));

  return { anyResponseFulfilled, completionRate, responseCounts, responseData };
};

export const readSurveyResponses = async ({
  ...params
}: ReadSurveyResponseParams): Promise<BarChartResponses['data']> => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/bar-chart',
  }).then(
    ({ data }: AxiosResponse<BarChartResponsesPayload>) =>
      transformSurveyResponses(data) as BarChartResponses['data']
  );
};

/**
 * read survey response counts
 */
export const readSurveyResponseCounts = async ({
  ...params
}: ReadSurveyResponseParams): Promise<SurveyResponsesCount> => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-response-counts',
  }).then(
    ({ data }: AxiosResponse<SurveyResponsesCountPayload>) =>
      transformObjectToCamelCase(data) as SurveyResponsesCount
  );
};

/**
 * read video responses
 */
export const readVideoResponses = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/video',
  }).then(({ data }) => transformVideoResponses(data));
};

/**
 * read completion rate
 */
export const readCompletionRate = async ({ ...params }: ReadSurveyResponseParams) => {
  return axios({
    ...getSurveyResponseRequestConfig(params),
    url: '/v2/api/survey-dashboard/survey-responses/completion-rate',
  }).then(({ data }) => camelizeData(data));
};
